import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useSubscription } from '@/hooks/useSubscription';
import { useIsAdmin } from '@/hooks/useIsAdmin';
import { signOut, sendVerificationEmail, authErrorMessage } from '@/lib/auth';
import { openBillingPortal } from '@/lib/stripe';
import { auth } from '@/lib/firebase';
import type { TierId } from '@/types';

const TIER_LABEL: Record<TierId, string> = {
  garage: 'Garage',
  restorer: 'Restorer',
  pro_shop: 'Pro Shop',
};

export default function Account() {
  const { user } = useAuth();
  const { subscription, isActive, loading: subLoading } = useSubscription();
  const { isAdmin, isAdminEmail, needsEmailVerification } = useIsAdmin();
  const navigate = useNavigate();
  const [portalBusy, setPortalBusy] = useState(false);
  const [portalErr, setPortalErr] = useState<string | null>(null);
  const [verifyBusy, setVerifyBusy] = useState(false);
  const [verifyMsg, setVerifyMsg] = useState<string | null>(null);
  const [verifyErr, setVerifyErr] = useState<string | null>(null);

  if (!user) return null;

  async function handleSignOut() {
    await signOut();
    navigate('/', { replace: true });
  }

  async function handlePortal() {
    setPortalErr(null);
    setPortalBusy(true);
    try {
      const url = await openBillingPortal();
      window.location.href = url;
    } catch (err) {
      setPortalErr(authErrorMessage(err));
      setPortalBusy(false);
    }
  }

  async function handleSendVerification() {
    if (!user) return;
    setVerifyErr(null);
    setVerifyMsg(null);
    setVerifyBusy(true);
    try {
      await sendVerificationEmail(user);
      setVerifyMsg('Sent. Check your inbox (and spam folder).');
    } catch (err) {
      setVerifyErr(authErrorMessage(err));
    } finally {
      setVerifyBusy(false);
    }
  }

  async function handleReloadAuth() {
    await auth.currentUser?.reload();
    window.location.reload();
  }

  return (
    <div className="app">
      <div className="main">
        <div className="eyebrow">Account · Profile & Billing</div>
        <h1 className="h-display">Account Settings</h1>
        <p className="lead" style={{ marginBottom: 28 }}>
          Your profile and subscription. Manage billing through Stripe's hosted portal — change
          payment method, cancel, switch plans, download invoices.
        </p>

        {needsEmailVerification && (
          <div className="auth-error" style={{ marginBottom: 24, padding: 18, display: 'grid', gridTemplateColumns: '1fr auto', gap: 18, alignItems: 'center' }}>
            <div>
              <strong style={{ color: 'var(--cream)', display: 'block', marginBottom: 4 }}>Admin access requires email verification.</strong>
              <span style={{ color: 'var(--text-dim)', fontFamily: 'var(--body)', fontSize: 13 }}>
                Your email matches an admin account, but it's not verified yet. Send the verification email, click the link in your inbox, then reload.
              </span>
              {verifyMsg && <div style={{ marginTop: 8, color: 'var(--good)', fontFamily: 'var(--mono)', fontSize: 12 }}>{verifyMsg}</div>}
              {verifyErr && <div style={{ marginTop: 8, color: 'var(--danger)', fontFamily: 'var(--mono)', fontSize: 12 }}>{verifyErr}</div>}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <button type="button" className="btn sm" onClick={handleSendVerification} disabled={verifyBusy}>
                {verifyBusy ? 'Sending…' : 'Send verify email'}
              </button>
              <button type="button" className="btn dark sm" onClick={handleReloadAuth}>I clicked it — reload</button>
            </div>
          </div>
        )}

        <div className="responsive-grid cols-2" style={{ marginBottom: 36 }}>
          <div className="card-elev">
            <h2 className="h-section">Profile</h2>
            <dl className="account-dl">
              <dt>Name</dt>
              <dd>{user.displayName ?? <em style={{ color: 'var(--text-muted)' }}>not set</em>}</dd>
              <dt>Email</dt>
              <dd style={{ fontFamily: 'var(--mono)', fontSize: 12.5, wordBreak: 'break-all' }}>{user.email}</dd>
              <dt>UID</dt>
              <dd style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--gold)', wordBreak: 'break-all' }}>{user.uid}</dd>
              <dt>Email verified</dt>
              <dd>
                {user.emailVerified ? (
                  <span className="badge live">✓ Verified</span>
                ) : (
                  <span className="badge reserved">Pending</span>
                )}
              </dd>
              <dt>Providers</dt>
              <dd style={{ fontFamily: 'var(--mono)', fontSize: 11 }}>
                {user.providerData.map((p) => p.providerId).join(', ') || '—'}
              </dd>
              <dt>Admin</dt>
              <dd>
                {isAdmin ? (
                  <Link to="/admin" className="badge oem" style={{ textDecoration: 'none' }}>✓ Admin → Console</Link>
                ) : isAdminEmail ? (
                  <span className="badge reserved">Pending verification</span>
                ) : (
                  <span className="badge sold">—</span>
                )}
              </dd>
            </dl>
            {!user.emailVerified && !needsEmailVerification && (
              <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px dashed var(--line-2)' }}>
                <button type="button" className="btn dark sm" onClick={handleSendVerification} disabled={verifyBusy}>
                  {verifyBusy ? 'Sending…' : 'Send verification email'}
                </button>
                {verifyMsg && <div style={{ marginTop: 8, color: 'var(--good)', fontFamily: 'var(--mono)', fontSize: 11 }}>{verifyMsg}</div>}
                {verifyErr && <div style={{ marginTop: 8, color: 'var(--danger)', fontFamily: 'var(--mono)', fontSize: 11 }}>{verifyErr}</div>}
              </div>
            )}
          </div>

          <div className="card-elev">
            <h2 className="h-section">Subscription</h2>
            {subLoading ? (
              <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>Loading…</div>
            ) : isActive && subscription ? (
              <>
                <dl className="account-dl">
                  <dt>Tier</dt>
                  <dd>
                    <span className="badge live" style={{ marginRight: 8 }}>
                      {subscription.tier ? TIER_LABEL[subscription.tier] : 'Active'}
                    </span>
                  </dd>
                  <dt>Status</dt>
                  <dd style={{ fontFamily: 'var(--mono)', fontSize: 12 }}>{subscription.status}</dd>
                  <dt>Renews</dt>
                  <dd style={{ fontFamily: 'var(--mono)', fontSize: 12 }}>
                    {subscription.currentPeriodEnd?.toDate
                      ? subscription.currentPeriodEnd.toDate().toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                        })
                      : '—'}
                    {subscription.cancelAtPeriodEnd && (
                      <span style={{ marginLeft: 8, color: 'var(--warning)' }}>(cancels at period end)</span>
                    )}
                  </dd>
                </dl>
                <div style={{ marginTop: 18 }}>
                  <button type="button" className="btn block" onClick={handlePortal} disabled={portalBusy}>
                    {portalBusy ? 'Opening…' : 'Manage Billing in Stripe →'}
                  </button>
                  {portalErr && <div className="auth-error" style={{ marginTop: 12 }}>{portalErr}</div>}
                </div>
              </>
            ) : (
              <div style={{ padding: '20px 0' }}>
                <div className="badge sold" style={{ marginBottom: 12 }}>No active subscription</div>
                <p style={{ color: 'var(--text-dim)', fontSize: 13.5, marginBottom: 18 }}>
                  Pick a tier to unlock the catalog. Stripe Checkout in test mode — use card
                  4242 4242 4242 4242 with any future expiry.
                </p>
                <button type="button" className="btn sm" onClick={() => navigate('/pricing')}>
                  View Tiers →
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="card-elev" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          <div>
            <div className="eyebrow" style={{ marginBottom: 4 }}>Session</div>
            <p style={{ fontSize: 13.5, color: 'var(--text-dim)' }}>
              You're signed in. Signing out drops the session on this device.
            </p>
          </div>
          <button type="button" className="btn danger sm" onClick={handleSignOut}>
            Sign Out
          </button>
        </div>
      </div>
    </div>
  );
}
